import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setAccountOpeningStep } from "../../redux/accountOpening";
import { useFormik } from "formik";
import { DomDocsUploadPayload } from "../../models/IEnums.model";
import "react-html5-camera-photo/build/css/index.css";
import { initialDomDocs } from "../../utils/constants";
import "youverify-web-sdk";
import DocUpload from "../DocUpload";
import UploadSignatureCard from "../UploadSignatureCard";
import SectionLabel from "../SectionLabel";
import CustomInputField from "../CustomInputField/CustomInputField";
import RadioBtn from "../../assets/icons/RadioBtn";
import apiHooks from "../../customHooks/apis";
import {
  extractValuesFromObj,
  handleNumericInput,
  handleNumericPaste,
  returnInputErrorMessage,
} from "../../utils/helpers";
import FormHelperText from "../FormHelperText";
import SignaturePad from "../SignaturePad";
import CustomModal from "../Modal/CustomModal";
import { domDocsValidationSchema } from "../../utils/validationSchemas";
import CustomDropdown from "../CustomDropdown";
import useUserValidated from "../../customHooks/useUserValidated";
import ImageUploadAndLivePhoto from "../ImageUploadAndLivePhoto";
import useValidateCurrency from "../../customHooks/useValidateCurrency";
import * as Yup from "yup";

function UploadDocumentSavingsDialog(props: any) {
  const dispatch = useDispatch();
  const {
    useGetMeansOfId,
    useUploadFile,
    useGetCurrencies,
    useUploadDomDocs,
    useUserDetails,
  } = apiHooks;
  const { idTypes } = useGetMeansOfId();
  const {
    currencies,
    handleGetGlCode,
    gettingCurrencies,
    getForeignCurrencies,
  } = useGetCurrencies();
  const { userDetails, handleUpdateUserDetails } = useUserDetails();
  const { handleUploadFile, uploadingFile } = useUploadFile();
  const { handleUploadDomDocs, uploadingDomDocs } = useUploadDomDocs();
  const { isValid } = useUserValidated();
  const formik = useFormik<DomDocsUploadPayload>({
    initialValues: initialDomDocs,
    validationSchema: domDocsValidationSchema.shape({
      imageUrl: isValid
        ? Yup.string().notRequired()
        : Yup.string().required("Image is required"),
    }),
    onSubmit: handleUploadDomDocs,
    validateOnBlur: true,
    validateOnMount: true,
    validateOnChange: true,
  });
  useValidateCurrency<DomDocsUploadPayload>(formik);
  const [docUrl, setDocUrl] = useState<{ drawn: string; uploaded: string }>({
    drawn: "",
    uploaded: "",
  });
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState(false);

  const goToStep = (step: any) => {
    handleUpdateUserDetails(formik.values);
    if (step === "update-detail") {
      localStorage.setItem("previous-screen", "document-upload");
    }
    dispatch(setAccountOpeningStep(step));
  };

  const radioBtnState = (state: boolean) => {
    return typeof formik.values.debitCardValueAddedService === "string"
      ? false
      : state;
  };

  const handleClickSave = (
    postedFilebase64: string,
    documentName: string,
    filetype: string,
    objectUrl: string,
  ) => {
    setDocUrl({ ...docUrl, drawn: objectUrl });
    setIsOpenModal(!isOpenModal);
    handleUploadFile(
      {
        postedFilebase64,
        documentName,
        filetype,
      },
      handleToggleModal,
    );
  };

  const handleToggleModal = (url: string) => {
    if (url) return formik.setFieldValue("signatureUrl", url);
    setIsOpenModal(!isOpenModal);
  };

  const handleRemoveSignature = (drawn: string) => {
    formik.setFieldValue("signatureUrl", drawn);
    setDocUrl({ ...docUrl, drawn });
  };

  const handleSelectCurrency = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    formik.setFieldValue("currencyOfAccountUSD", "");
    formik.setFieldValue("currencyOfAccountGBP", "");
    formik.setFieldValue("currencyOfAccountEURO", "");
    const { value } = event.target;
    if (!value) return;
    const selectedCurrency = currencies?.find(
      (item) => String(item.currencY_CODE) === value.toString(),
    );
    if (!selectedCurrency) return;
    const currencyShort = selectedCurrency.brieF_DESC_ENG || "";
    localStorage.setItem("currency", currencyShort);
    const fieldName = `currencyOfAccount${
      +value == 978 ? `${currencyShort}O` : currencyShort
    }`;
    formik.setFieldValue(fieldName, value.toString());
    formik
      .setFieldValue("accountTypecode", handleGetGlCode(+value))
      .then(() => {
        const { accountTypecode, ...rest } = formik.errors;
        formik.setErrors(rest);
      });
  };

  useEffect(() => {
    const accTypeFromStorage = localStorage.getItem("altFinanceAccountType");
    formik.setValues({
      ...initialDomDocs,
      ...extractValuesFromObj(initialDomDocs, userDetails),
      accountTypeName: accTypeFromStorage || "",
      debitCardValueAddedService: true,
      imageUrl: localStorage.getItem("userImage") || "",
      idCardNumber: userDetails.nin || "",
    });
  }, [idTypes, userDetails]);

  useEffect(() => {
    const signatureUrl = docUrl.uploaded;
    if (signatureUrl) {
      formik.setValues({
        ...formik.values,
        signatureUrl,
      });
    }
  }, [docUrl.uploaded]);

  return (
    <div>
      {/* {loading && <Loader />} */}
      <form onSubmit={formik.handleSubmit}>
        <div className=" card-form pl-1 pr-1 ">
          <div className="card-body">
            <h4 className="card-title text-center pl-3 mt-5">
              Document upload
            </h4>
            <p className="text-center">
              <small>
                The file Format should be JPEG, PNG or PDF (max size:2mb)
              </small>
            </p>
            <FormHelperText />
            <div className="d-flex align-items-end flex-column flex-lg-row w-100 gap-3">
              <div className="d-flex flex-column flex-lg-row align-items-end w-lg-50 w-100 gap-2">
                <UploadSignatureCard
                  uploading={uploadingFile}
                  imageUrl={docUrl.drawn}
                  required
                  handleRemoveFile={handleRemoveSignature}
                  width="w-lg-50 w-100"
                  show={!docUrl.uploaded}
                  label="Draw/Upload Signature"
                  onClickCard={() => handleToggleModal("")}
                  disabled={isUploading}
                />
                <div className="w-lg-50 w-100">
                  <DocUpload
                    required
                    show={!docUrl.drawn}
                    getFileUrl={(_, uploaded) =>
                      setDocUrl({ ...docUrl, uploaded })
                    }
                    setIsUploading={setIsUploading}
                    disabled={uploadingFile}
                  />
                </div>
              </div>

              <ImageUploadAndLivePhoto
                getFileUrl={(url) => formik.setFieldValue("imageUrl", url)}
              />
            </div>
            <div className="my-3">
              <DocUpload
                name="utilityBill"
                required
                show
                label="Upload utility bill (not older than 3 months)"
                getFileUrl={formik.setFieldValue}
              />
            </div>
            <SectionLabel label="Means of Identification Document" />

            <div className="d-flex flex-column gap-3">
              <div className="d-flex flex-column flex-lg-row align-items-end w-100 gap-3">
                <div className="w-lg-50 w-100">
                  <CustomInputField
                    label="Identity Card Type"
                    value="National Identity Card"
                    required
                    disabled
                  />
                </div>
                <div className="w-lg-50 w-100">
                  <CustomInputField
                    required
                    label="National Identity Number (NIN)"
                    placeholder="Enter ID Number"
                    name="idCardNumber"
                    onKeyDown={(e) =>
                      handleNumericInput(e, formik.values.identityCardType)
                    }
                    onPaste={(e) =>
                      handleNumericPaste(e, formik.values.identityCardType)
                    }
                    value={formik.values.idCardNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    errorMessage={
                      returnInputErrorMessage<typeof formik.initialValues>(
                        formik,
                        "idCardNumber",
                      ) as string
                    }
                    disabled
                  />
                </div>
              </div>
              <DocUpload
                name="frontPageIdUrl"
                required
                show
                label="Front page of ID"
                bottomText="The file format should be JPEG, PNG or PDF  (max size: 2mb)"
                getFileUrl={formik.setFieldValue}
                errorMessage=""
              />
              <div className="d-flex flex-column flex-lg-row mb-3 align-items-end gap-3">
                <CustomDropdown
                  required
                  label="Currencies of account to be opened"
                  placeholder="Select currency"
                  extraClasses="col-lg-6"
                  options={getForeignCurrencies()}
                  labelKey="lonG_DESC_ENG"
                  valueKey="currencY_CODE"
                  onClickOption={handleSelectCurrency}
                  onBlur={formik.handleBlur}
                  errorMessage={
                    formik.touched["currency"] ? formik.errors["currency"] : ""
                  }
                  name="currency"
                  formik={formik}
                  disabled={gettingCurrencies}
                />
                <div className="d-flex flex-column gap-2 w-lg-50 w-100">
                  <small className="fw-medium small_text">
                    Would you like to have a Debit Card (Verve)
                  </small>
                  <div className="w-100 d-flex justify-content-between">
                    <span className="w-50 d-flex gap-2 fs-6 fw-medium">
                      <RadioBtn
                        color="#181717"
                        checked={!!formik.values.debitCardValueAddedService}
                        onClickCheck={() =>
                          formik.handleChange({
                            target: {
                              name: "debitCardValueAddedService",
                              value: true,
                            },
                          })
                        }
                      />
                      Yes
                    </span>
                    <span className="w-50 d-flex gap-2 fs-6 fw-medium">
                      <RadioBtn
                        color="#181717"
                        checked={!formik.values.debitCardValueAddedService}
                        onClickCheck={() =>
                          formik.handleChange({
                            target: {
                              name: "debitCardValueAddedService",
                              value: false,
                            },
                          })
                        }
                      />
                      No
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* --------------- */}
          </div>
          <div className="d-flex justify-content-end mt-2 gap-lg-4 gap-2">
            <div className="mb-3">
              <button
                type="button"
                onClick={() => {
                  goToStep("update-detail");
                }}
                className="btn btn-outline-gold mr-3 btn-filled-white"
              >
                Previous
              </button>
            </div>
            {uploadingDomDocs ? (
              <div className="spinner-border text-gold" role="status">
                <span className="sr-only"></span>
              </div>
            ) : (
              <button
                type="submit"
                className="btn btn-filled-red"
                disabled={!formik.isValid}
              >
                Proceed
              </button>
            )}
          </div>
        </div>
      </form>
      <CustomModal
        show={isOpenModal}
        onClickClose={() => handleToggleModal("")}
      >
        <SignaturePad onClickSave={handleClickSave} />
      </CustomModal>
    </div>
  );
}

export default UploadDocumentSavingsDialog;
